import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useDragScroll } from "@/hooks/useDragScroll";
import { cn } from "@/lib/utils";

interface Profile {
  id: string;
  name: string;
  slug: string;
  logo: string;
  category: string;
}

interface TopProfilesProps {
  title: string;
  profiles: Profile[];
}

export const TopProfiles = ({ title, profiles }: TopProfilesProps) => {
  const [isPaused, setIsPaused] = useState(false);
  
  const { scrollRef, isDragging, containerProps } = useDragScroll({
    speed: 1.2,
    onDragStart: () => setIsPaused(true),
    onDragEnd: () => setTimeout(() => setIsPaused(false), 2000),
  });

  // Duplicate profiles for seamless infinite scroll
  const displayProfiles = profiles.slice(0, 10);
  const allProfiles = [...displayProfiles, ...displayProfiles, ...displayProfiles];

  // Calculate single set width
  const getSingleSetWidth = useCallback(() => {
    if (!scrollRef.current) return 0;
    const containerWidth = scrollRef.current.offsetWidth;
    const isMobile = window.innerWidth < 1024;
    const itemWidth = containerWidth / (isMobile ? 3.5 : 6.5);
    return itemWidth * displayProfiles.length;
  }, [displayProfiles.length]);

  // Auto-scroll animation - use ref to track position without restarting
  const scrollPositionRef = useRef(0);
  const isInitializedRef = useRef(false);
  
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let animationId: number;
    
    // Initialize scroll position only once
    if (!isInitializedRef.current) {
      scrollPositionRef.current = getSingleSetWidth();
      container.scrollLeft = scrollPositionRef.current;
      isInitializedRef.current = true;
    }

    const animate = () => {
      if (!isPaused && !isDragging && container) {
        scrollPositionRef.current += 0.5;
        
        // Reset seamlessly when scrolled through one set
        const singleSetWidth = getSingleSetWidth();
        if (scrollPositionRef.current >= singleSetWidth * 2) {
          scrollPositionRef.current = singleSetWidth;
        }
        
        container.scrollLeft = scrollPositionRef.current;
      } else if (container) {
        // Sync position when paused/dragging
        scrollPositionRef.current = container.scrollLeft;
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isPaused, isDragging, getSingleSetWidth]);

  return (
    <section className="py-8 lg:py-6 border-b border-border/50 overflow-hidden">
      <div className="container mx-auto px-4">
        <p className="text-xs lg:text-sm font-bold uppercase tracking-widest text-center mb-6">
          Na Merch.sk nájdeš produkty{" "}
          <span className="bg-gradient-to-r from-primary via-[hsl(170,70%,35%)] to-[hsl(170,60%,45%)] bg-clip-text text-transparent">
            Koza Bobkov
          </span>
        </p>

        {/* Logos with gradient fade on sides */}
        <div className="relative mb-4">
          {/* Left gradient */}
          <div className="absolute left-0 top-0 bottom-0 w-12 lg:w-20 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          {/* Right gradient */}
          <div className="absolute right-0 top-0 bottom-0 w-12 lg:w-20 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          
          <div 
            {...containerProps}
            className={cn(
              "flex overflow-x-auto scrollbar-hide select-none",
              isDragging ? "cursor-grabbing" : "cursor-grab"
            )}
            onMouseEnter={() => !isDragging && setIsPaused(true)}
            onMouseLeave={() => {
              containerProps.onMouseLeave();
              if (!isDragging) setIsPaused(false);
            }}
          >
            {allProfiles.map((profile, index) => (
              <Link
                key={`${profile.id}-${index}`}
                to={`/interpret/${profile.slug}`}
                className={cn(
                  "flex-shrink-0 px-3 lg:px-4 group w-[calc(100%/3.5)] lg:w-[calc(100%/6.5)]",
                  "transition-all duration-200",
                  isDragging && "opacity-80"
                )}
                title={profile.name}
                draggable={false}
              >
                <div className="h-10 lg:h-12 flex items-center justify-center grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-500">
                  <img
                    src={profile.logo}
                    alt={profile.name}
                    className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-500 pointer-events-none"
                    draggable={false}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA under logos */}
        <div className="flex justify-center">
          <Link 
            to="/interpreti" 
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors group"
          >
            Prezrieť značky
            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};
